/**
 * 语言偏好工具 - 完全基于 IP 识别语言，每次访问都重新获取
 * 设计原则：语言完全由 IP 决定，忽略浏览器缓存
 */

// 仅用于手动切换后临时保存用户选择（页面刷新后会根据新 IP 重新识别）
const LOCALE_STORAGE_KEY = 'locale'
const LOCALE_SOURCE_STORAGE_KEY = 'localeSource'

export const DEFAULT_LOCALE = 'en'
export const LOCALE_SOURCE_AUTO = 'auto'
export const LOCALE_SOURCE_MANUAL = 'manual'

const VALID_LOCALES = Object.freeze(['zh-TW', 'en', 'ko'])

function canUseStorage() {
  return typeof window !== 'undefined' && !!window.localStorage
}

function readStorage(key) {
  if (!canUseStorage()) {
    return ''
  }
  return window.localStorage.getItem(key) || ''
}

function writeStorage(key, value) {
  if (!canUseStorage()) {
    return
  }
  window.localStorage.setItem(key, value)
}

function normalizeLocale(locale) {
  const normalized = String(locale || '').trim()
  return VALID_LOCALES.includes(normalized) ? normalized : ''
}

function setLocaleSource(source) {
  writeStorage(LOCALE_SOURCE_STORAGE_KEY, source)
}

export function getStoredLocale() {
  return normalizeLocale(readStorage(LOCALE_STORAGE_KEY))
}

function getLocaleSource() {
  return readStorage(LOCALE_SOURCE_STORAGE_KEY)
}

/**
 * 检查用户是否明确选择了手动语言
 */
function hasManualLocalePreference() {
  return getLocaleSource() === LOCALE_SOURCE_MANUAL && !!getStoredLocale()
}

/**
 * 初始化语言：完全忽略缓存，始终使用默认语言
 * 实际语言将在 InitRegionContext 后根据 IP 重新设置
 */
export function resolveInitialLocale() {
  return DEFAULT_LOCALE
}

/**
 * 手动切换语言：用户主动选择时保存，页面刷新后仍会被 IP 识别覆盖
 */
export function setManualLocalePreference(locale) {
  const normalizedLocale = normalizeLocale(locale)
  if (!normalizedLocale) {
    return ''
  }
  writeStorage(LOCALE_STORAGE_KEY, normalizedLocale)
  setLocaleSource(LOCALE_SOURCE_MANUAL)
  return normalizedLocale
}

/**
 * 根据 IP 自动设置语言：每次都基于传入的 autoLocale 更新
 * 不再参考 localStorage 缓存
 */
export function resolvePreferredLocale(autoLocale) {
  const normalizedAutoLocale = normalizeLocale(autoLocale) || DEFAULT_LOCALE

  // 完全基于 IP 自动识别，不再参考浏览器缓存
  // 手动选择语言只影响当前会话，刷新后会被新 IP 识别覆盖
  if (hasManualLocalePreference()) {
    // 记录手动选择的语言，但不阻止 IP 识别
    const manualLocale = getStoredLocale()
    writeStorage(LOCALE_STORAGE_KEY, manualLocale)
    setLocaleSource(LOCALE_SOURCE_MANUAL)
    // 返回手动选择的语言，让用户手动切换生效
    return manualLocale
  }

  // 直接使用 IP 识别的语言结果
  writeStorage(LOCALE_STORAGE_KEY, normalizedAutoLocale)
  setLocaleSource(LOCALE_SOURCE_AUTO)
  return normalizedAutoLocale
}

/**
 * 强制使用 IP 识别的语言，忽略任何手动选择
 * 用于每次页面加载时强制刷新语言
 */
export function forceLocaleFromIP(autoLocale) {
  const normalizedAutoLocale = normalizeLocale(autoLocale) || DEFAULT_LOCALE
  writeStorage(LOCALE_STORAGE_KEY, normalizedAutoLocale)
  setLocaleSource(LOCALE_SOURCE_AUTO)
  return normalizedAutoLocale
}
